import { Controller } from "@hotwired/stimulus"
import { createAlert } from "../scripts/createAlert"

export default class extends Controller {
  static targets = []
  static values = {}

  intervals = {};

  successMessages = {
    "HistoricalTrendPlayerJob": "Player trend analysis completed successfully.",
    "HistoricalTrendTeamJob": "Team trend analysis completed successfully.",
    "RecapJob": "Recap generated successfully.",
    "BioJob": "Bio generated successfully.",
    "BioBatchJob": "Bios generated successfully.",
    "RosterImportJob": "Roster is ready for review.",
    "ExtractStatsJob": "Non-xml player stats extracted successfully.",
    "NominationDraftBatchJob": "Nominations generated successfully.",
    "NominationDraftJob": "Nomination draft generated successfully.",
    "SummaryDraftJob": "Summary draft generated successfully.",
    "StoryDraftJob": "Story draft generated successfully.",
    "FormImportJob": "Form responses imported successfully.",
    "FormBioJob": "Bios generated successfully.",
    "RecordBookImportJob": "Record book pages are ready. Draw boxes into buckets.",
    "RecordBookNormalizeJob": "Record lists are ready to review.",
    "RecordBookAggregateJob": "Record book stats updated successfully.",
    "RecordBookCompareJob": "Record book comparison completed successfully.",
    "BeatFinderJob": "Key plays identified successfully."
  }

  errorMessages = {
    "HistoricalTrendPlayerJob": "Error when doing player trend analysis.",
    "HistoricalTrendTeamJob": "Error when doing team trend analysis.",
    "RecapJob": "Error while generating recap.",
    "BioJob": "Error while generating bio.",
    "BioBatchJob": "Error while generating bios.",
    "RosterImportJob": "Error while importing roster.",
    "ExtractStatsJob": "Error while extracting non-xml player stats.",
    "NominationDraftBatchJob": "Error while generating nominations.",
    "NominationDraftJob": "Error while generating nomination draft.",
    "SummaryDraftJob": "Error while generating summary draft.",
    "StoryDraftJob": "Error while generating story draft.",
    "FormImportJob": "Error while importing form responses.",
    "FormBioJob": "Error while generating form bio.",
    "RecordBookImportJob": "Error while preparing the record book PDF.",
    "RecordBookNormalizeJob": "Error while normalizing the record book text.",
    "RecordBookAggregateJob": "Error while updating record book stats.",
    "RecordBookCompareJob": "Error while comparing stats to the record book.",
    "BeatFinderJob": "Error while identifying key plays."
  }

  connect() {
    // console.log("Hello, Stimulus from delayed-job-watcher!", this.element)

    const watching_jobs = JSON.parse(localStorage.getItem('delayed_jobs')) || [];

    const params = new URLSearchParams(window.location.search);
    const job_id = params.get('job_id');
    if (job_id) {
      watching_jobs.push(job_id);
      localStorage.setItem('delayed_jobs', JSON.stringify(watching_jobs));
    }

    if (watching_jobs.length > 0) {
      this.startJobWatchingIntervals(watching_jobs);
    }
  }

  startJobWatchingIntervals(job_ids) {

    job_ids.forEach(async (job_id) => {

      // check to see if it actually needs to be watched
      // if we get a status === 'not_found' then we can remove it from the list
      const data = await this.makeRequest(job_id);
      if (data.status === 'not_found') {
        console.log(`Job ${job_id} does not exist, removing from list`);

        // remove it from localStorage
        this.removeJobIdFromLocalStorage(job_id);
        this.removeJobIdFromUrl();

        return;
      }

      // Refresh progressive RecapJob UI from absolute created_at on the immediate request
      this.updateProgressiveUiFromJobData(data);

      const interval = setInterval(() => {
        this.fetchSingleJobStatus(job_id);
      }, 5000);

      this.intervals[job_id] = interval;
    });

  }

  async fetchSingleJobStatus(job_id) {

    const data = await this.makeRequest(job_id);

    if (data.status === 'completed' || data.status === 'failed') {
      // remove it from the interval
      if (this.intervals[job_id]) {
        clearInterval(this.intervals[job_id]);
        this.intervals[job_id] = null
      }

      // remove it from localStorage
      this.removeJobIdFromLocalStorage(job_id);

      // remove it from the URL fragment
      this.removeJobIdFromUrl();

      const isReloading = `${window.location.pathname}${window.location.search}` === data.trackable.redirect_route;

      // the job is done
      let message, immediate_type, cookie_type;
      if (data.status === 'completed') {
        message = this.buildSuccessMessage(data.trackable, isReloading);
        immediate_type = 'info';
        cookie_type = 'notice';
      } else {
        message = this.buildErrorMessage(data.trackable, isReloading);
        immediate_type = 'danger';
        cookie_type = 'danger';
      }

      // if they are the same page that is the redirect, just reload the page for them
      if (isReloading) {
        // set flash to present after the page reload
        this.setFlashMessageAsCookie(message, cookie_type);

        // reload the page
        window.location.reload();
      } else {
        this.removeDelayedJobUiElements();

        createAlert(immediate_type, message, true);
      }

    }

    if ((data.status === 'pending' || data.status === 'processing') && data.trackable && data.trackable.updated_at) {
      // Keep progressive wait UI in sync using TrackedJob.created_at (no second timer)
      this.updateProgressiveUiFromJobData(data);

      // check for long running jobs and cancel them
      const updated_at = new Date(data.trackable.updated_at);
      const now = new Date();
      const diff = now - updated_at;
      const diffInMinutes = Math.floor(diff / 1000 / 60);
      if (diffInMinutes > 10) {
        console.log(`Job ${job_id} has been running for more than 10 minutes, cancelling it`);

        // remove it from the interval
        if (this.intervals[job_id]) {
          clearInterval(this.intervals[job_id]);
          this.intervals[job_id] = null;
        }

        // remove it from localStorage
        this.removeJobIdFromLocalStorage(job_id);

        // remove it from the URL fragment
        this.removeJobIdFromUrl();
      }
    }

  }

  // Update RecapJob (or any progressive) spinner from TrackedJob.created_at on each poll
  updateProgressiveUiFromJobData(data) {
    if (!data || !data.trackable) return;
    if (data.status !== 'pending' && data.status !== 'processing') return;
    if (!data.trackable.created_at) return;

    const jobId = data.trackable.job_id;
    const container = document.querySelector(
      `.delayed-job-component[data-delayed-job-progressive="true"][data-job-id="${jobId}"]`
    );
    if (!container) return;

    let messages;
    try {
      messages = JSON.parse(container.dataset.progressiveMessages || '[]');
    } catch (e) {
      console.error('Error parsing progressive messages:', e);
      return;
    }
    if (!Array.isArray(messages) || messages.length === 0) return;

    const intervalSeconds = parseInt(container.dataset.progressiveIntervalSeconds, 10) || 5;
    const startedAt = new Date(data.trackable.created_at);
    const elapsedSeconds = (Date.now() - startedAt.getTime()) / 1000;
    let stepIndex = Math.floor(elapsedSeconds / intervalSeconds);
    stepIndex = Math.min(Math.max(stepIndex, 0), messages.length - 1);

    const percent = Math.round(((stepIndex + 1) / messages.length) * 100);
    const stepLabel = `${stepIndex + 1}/${messages.length}`;
    const message = messages[stepIndex];

    const stepLabelElement = container.querySelector('[data-progressive-step-label]');
    const messageElement = container.querySelector('[data-progressive-message]');
    const progressBarElement = container.querySelector('[data-progressive-bar]');
    const progressElement = container.querySelector('[data-progressive-progressbar]');

    if (stepLabelElement) stepLabelElement.textContent = stepLabel;
    if (messageElement) messageElement.textContent = message;
    if (progressBarElement) progressBarElement.style.width = `${percent}%`;
    if (progressElement) progressElement.setAttribute('aria-valuenow', String(percent));
  }

  makeRequest(job_id) {
    return fetch(`/jobs/${job_id}`).then(response => response.json()).then(data => {
      return data;
    }).catch(e => {
      console.error('Error:', e);

      // console.error('Error:', error);
      createAlert('danger', 'An error occurred while fetching the job status.', true);

      // remove it from the interval
      this.intervals[job_id] && clearInterval(this.intervals[job_id]);

      return { error: e.message };
    });
  }

  removeJobIdFromLocalStorage(job_id) {
    const watching_jobs = JSON.parse(localStorage.getItem('delayed_jobs'));
    const index = watching_jobs.indexOf(job_id);
    watching_jobs.splice(index, 1);
    localStorage.setItem('delayed_jobs', JSON.stringify(watching_jobs));
  }

  removeJobIdFromUrl() {
    // remove from the URL fragment
    const urlObj = new URL(window.location);

    // Access the search parameters
    const params = urlObj.searchParams;

    // Remove the key
    params.delete('job_id');

    // Return the updated URL as a string
    window.history.replaceState(null, '', urlObj.toString());
  }

  buildSuccessMessage(trackable, isReloading) {
    let message = trackable && this.successMessages[trackable.job_type] || 'Job completed successfully.';
    if (!isReloading && trackable.redirect_route) {
      message += ` <a href="${trackable.redirect_route}">View</a>`;
    }

    return message;
  }

  buildErrorMessage(trackable, isReloading) {
    let message = trackable && this.errorMessages[trackable.job_type] || 'Job has failed.';
    if (!isReloading && trackable.redirect_route) {
      message += ` <a href="${trackable.redirect_route}">View</a>`;
    }

    return message;
  }

  setFlashMessageAsCookie(message, type = 'info') {
    document.cookie = `flash_${type}=${message}; path=/`;
  }

  removeDelayedJobUiElements() {
    var elements = document.querySelectorAll('.delayed-job-component');
    elements.forEach(element => {
      element.remove();
    });
  }

}

